import { ref, readonly } from 'vue'
import { useCountryDetection } from './useCountryDetection'
import { findPhoneCodeByIsoCode, findPhoneCodeByIsoCodeWithDefault } from '@/composables/phone/detect/usePhoneCodeFromCountry'
import type { PhoneCode } from '@/constants/phone-codes'

/**
 * Глобальный стор для хранения определенной страны клиента
 * Используется для автодетекта телефонного кода в формах
 */
const countryIsoCode = ref<string | null>(null)
const countryPhoneCode = ref<PhoneCode | null>(null)
const isDetecting = ref(false)
const isDetected = ref(false)

/**
 * Composable для работы с глобальным стором страны
 * @returns Реактивные refs и методы для работы со страной
 */
export function useCountryStore() {
  const { detectCountry, resetDetection: resetCountryDetection } = useCountryDetection()

  /**
   * Определяет страну клиента и сохраняет в стор
   * Вызывается один раз при инициализации приложения
   * @param useApiFallback - Использовать API запрос как fallback, если CDN заголовки недоступны
   */
  const detectAndStoreCountry = async (useApiFallback: boolean = true): Promise<void> => {
    if (isDetected.value || isDetecting.value) {
      return
    }

    // Site's own country from app.config.site (per locale site: ua/by/pl/am).
    // Dial code derives from the ISO via findPhoneCodeByIsoCode — no separate code stored.
    const site = useAppConfig().site as { defaultCountryIso?: string, geoDetect?: boolean } | undefined
    const siteIso = site?.defaultCountryIso ?? null

    isDetecting.value = true

    try {
      // Locale sites (geoDetect falsy) are single-country → force the site's country,
      // ignoring the visitor's geo-IP (which showed UA on every site).
      if (siteIso && !site?.geoDetect) {
        countryIsoCode.value = siteIso.toUpperCase()
        countryPhoneCode.value = findPhoneCodeByIsoCode(siteIso) ?? findPhoneCodeByIsoCodeWithDefault(siteIso, '380')
        isDetected.value = true
        return
      }

      // Flagship (geoDetect) → detect the visitor.
      // countryIsoCode stays null if detection fails — prevents region dropdown from loading
      // Ukrainian oblasts for international users (phone code still defaults to site country).
      const detectedIsoCode = await detectCountry(useApiFallback)
      countryIsoCode.value = detectedIsoCode ?? null
      countryPhoneCode.value = findPhoneCodeByIsoCodeWithDefault(detectedIsoCode ?? siteIso, '380')
      isDetected.value = true
    } catch (error) {
      console.error('[useCountryStore] Ошибка при определении страны:', error)
      countryIsoCode.value = null
      countryPhoneCode.value = findPhoneCodeByIsoCodeWithDefault(siteIso, '380')
      isDetected.value = true
    } finally {
      isDetecting.value = false
    }
  }

  /**
   * Сбрасывает определенную страну
   * Полезно для тестирования или принудительного переопределения
   */
  const resetCountry = () => {
    countryIsoCode.value = null
    countryPhoneCode.value = null
    isDetected.value = false
    isDetecting.value = false
    resetCountryDetection()
  }

  /**
   * Устанавливает страну вручную.
   * Также вызывается из useAppInit при fallback API (аккаунт по detect-by-country → UA).
   * @param isoCode - ISO код страны
   */
  const setCountry = (isoCode: string | null) => {
    countryIsoCode.value = isoCode
    if (isoCode) {
      const phoneCode = findPhoneCodeByIsoCodeWithDefault(isoCode, '380')
      countryPhoneCode.value = phoneCode
    } else {
      const defaultPhoneCode = findPhoneCodeByIsoCodeWithDefault(null, '380')
      countryPhoneCode.value = defaultPhoneCode
    }
    isDetected.value = true
  }

  return {
    // Только для чтения
    countryIsoCode: readonly(countryIsoCode),
    countryPhoneCode: readonly(countryPhoneCode),
    isDetecting: readonly(isDetecting),
    isDetected: readonly(isDetected),
    // Методы
    detectAndStoreCountry,
    resetCountry,
    setCountry,
  }
}
